import HeroScrollCue from "./HeroScrollCue.web";
import UseCaseFilm from "./UseCaseFilm.web";
import React, { useState } from "react";
import { Link } from "expo-router";
import MetaHead from "@/components/MetaHead";
import PublicFooter from "@/components/PublicFooter";
import films from "@/constants/useCaseFilms.json";
import {
  FREE_USER_STARTING_CREDITS_DISPLAY,
  GAB_AI_SOFTWARE_ID,
} from "@/constants/config";
import useBusinessEnrollment from "@/components/Business/useBusinessEnrollment";
import { businessCopy } from "@/constants/businessContent";
import "./ChurchesPage.css";
import "./UseCaseEditorial.css";

function Cta({ href, children, secondary = false }) {
  return (
    <Link
      className={`church-cta${secondary ? " church-cta-secondary" : ""}`}
      href={href}
    >
      {children}
      <span aria-hidden="true">↗</span>
    </Link>
  );
}
export function editorialSchema(data) {
  const url = `https://gab.ai/use-cases/${data.slug}`;
  const graph = [
    {
      "@type": "WebPage",
      "@id": `${url}#webpage`,
      name: data.seo?.title || data.name,
      description: data.seo?.description || data.subtitle,
      url,
      isPartOf: { "@type": "WebSite", name: "Gab AI", url: "https://gab.ai" },
      inLanguage: "en-US",
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        ["Home", "https://gab.ai"],
        ["Use cases", "https://gab.ai/use-cases"],
        [data.name, url],
      ].map(([name, item], i) => ({
        "@type": "ListItem",
        position: i + 1,
        name,
        item,
      })),
    },
    {
      "@type": "SoftwareApplication",
      "@id": GAB_AI_SOFTWARE_ID,
      name: "Gab AI",
      applicationCategory: "MultimediaApplication",
      operatingSystem: "Web, iOS, Android, macOS",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    },
  ];
  const film = films[data.slug];
  if (film)
    graph.push({
      "@type": "VideoObject",
      name: `Gab AI for ${data.name}`,
      description: film.description,
      thumbnailUrl: `https://gab.ai${film.poster}`,
      contentUrl: `https://gab.ai${film.src}`,
      uploadDate: film.uploadDate,
      duration: `PT${film.duration}S`,
    });
  if (data.tools?.length)
    graph.push({
      "@type": "ItemList",
      name: `AI tools for ${data.name}`,
      itemListElement: data.tools.map((tool, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: tool.name,
        description: tool.desc,
      })),
    });
  if (data.faqs?.length)
    graph.push({
      "@type": "FAQPage",
      mainEntity: data.faqs.map((faq) => ({
        "@type": "Question",
        name: faq.q,
        acceptedAnswer: { "@type": "Answer", text: faq.a },
      })),
    });
  return { "@context": "https://schema.org", "@graph": graph };
}
export default function UseCaseEditorial({ data, relatedUseCases = [] }) {
  const [copied, setCopied] = useState(null);
  const [copyError, setCopyError] = useState(false);
  const enrollment = useBusinessEnrollment();
  const business = businessCopy(enrollment);
  const film = films[data.slug];
  const lines = String(data.headline || data.name).split("\n");
  async function copyPrompt(prompt, index) {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(index);
      setCopyError(false);
    } catch {
      setCopyError(true);
    }
  }
  return (
    <div className="church-page uc-editorial">
      <MetaHead
        title={data.seo?.title || `AI for ${data.name} | Gab AI`}
        description={data.seo?.description || data.subtitle}
        canonical={`/use-cases/${data.slug}`}
        keywords={data.seo?.keywords}
        image={
          data.seo?.ogImage ||
          `https://gab.ai${film?.poster || "/brand-assets/gab-ai-mark-black.svg"}`
        }
        structuredData={editorialSchema(data)}
      />
      <main
        id="main-content"
        className="church-scroll"
        data-landing-scroll="true"
      >
        <div className="church-content">
          <header className="church-hero">
            <p className="church-eyebrow">
              Gab AI for {data.name.toLowerCase()}
            </p>
            <h1>
              {lines.map((line, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <br />}
                  {i === 0 ? line : <span>{line}</span>}
                </React.Fragment>
              ))}
            </h1>
            <p className="church-intro">{data.subtitle || data.description}</p>
            <div className="church-actions">
              <Cta href="/signup">{data.cta || "Try Gab AI free"}</Cta>
              <Cta href="/business" secondary>
                Explore team plans
              </Cta>
            </div>
            <p className="church-fine">
              {FREE_USER_STARTING_CREDITS_DISPLAY} free credits · No credit card
              required to try Gab AI
            </p>
            <HeroScrollCue href={data.painPoints?.length ? "#uc-challenges" : "#uc-tools"} />
            {film && (
              <figure className="church-film">
                <UseCaseFilm
                  src={film.src}
                  poster={film.poster}
                  label={`Gab AI for ${data.name}: narrated tool overview`}
                >
                  <track
                    kind="captions"
                    src={film.captions}
                    srcLang="en"
                    label="English"
                  />
                  <p>
                    <a href={film.src}>Download the overview video</a>
                  </p>
                </UseCaseFilm>
                <figcaption>
                  <span>Made for {data.name.toLowerCase()}.</span>
                  <span>
                    {film.duration}-second narrated overview · English captions
                  </span>
                </figcaption>
              </figure>
            )}
          </header>
          {!!data.painPoints?.length && (
            <section className="church-section" aria-labelledby="uc-challenges">
              <div className="church-section-head">
                <p className="church-eyebrow">A better starting point</p>
                <h2 id="uc-challenges">
                  Less friction.
                  <br />
                  More forward.
                </h2>
              </div>
              <div className="uc-challenges">
                {data.painPoints.map((point, i) => (
                  <article key={i}>
                    <span className="church-number">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h3>{point.problem}</h3>
                    <p>{point.solution}</p>
                  </article>
                ))}
              </div>
            </section>
          )}
          <section className="church-section" aria-labelledby="uc-tools">
            <div className="church-section-head">
              <p className="church-eyebrow">Your work, with a head start</p>
              <h2 id="uc-tools">
                Tools for
                <br />
                {data.name.toLowerCase()}.
              </h2>
              <p>
                Bring the idea. Give Gab AI the details. Make the result your
                own.
              </p>
            </div>
            <div className="uc-tool-grid">
              {(data.tools || []).map((tool, i) => (
                <article key={tool.name}>
                  <span className="church-number">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3>{tool.name}</h3>
                  <p>{tool.desc}</p>
                </article>
              ))}
            </div>
            {!!data.capabilities?.length && (
              <ul className="uc-capabilities">
                {data.capabilities.map((text) => (
                  <li key={text}>{text}</li>
                ))}
              </ul>
            )}
          </section>
          {!!data.examplePrompts?.length && (
            <section className="church-section" aria-labelledby="uc-prompts">
              <div className="church-section-head">
                <p className="church-eyebrow">Start with something real</p>
                <h2 id="uc-prompts">
                  A few ideas
                  <br />
                  to get you going.
                </h2>
                <p>Copy a prompt, add your details, and try it in Gab AI.</p>
              </div>
              <div className="uc-prompts">
                {data.examplePrompts.map((item, i) => (
                  <details
                    key={`${item.title}-${i}`}
                    open={i === 0 ? true : undefined}
                  >
                    <summary>
                      {item.title}
                      <span aria-hidden="true">+</span>
                    </summary>
                    <p>{item.prompt}</p>
                    <button
                      type="button"
                      onClick={() => copyPrompt(item.prompt, i)}
                    >
                      {copied === i ? "Copied" : "Copy prompt"}
                    </button>
                  </details>
                ))}
              </div>
              <p aria-live="polite" className="church-fine">
                {copyError
                  ? "Copy is unavailable. Select the prompt text to copy it."
                  : copied !== null
                    ? "Prompt copied. Paste it into Gab AI to begin."
                    : ""}
              </p>
            </section>
          )}
          <section
            className="church-section uc-business"
            aria-labelledby="uc-business"
          >
            <div className="church-section-head">
              <p className="church-eyebrow">Gab AI for Business</p>
              <h2 id="uc-business">
                Good on your own.
                <br />
                Together as a team.
              </h2>
              <p>
                For organizations of 2–250, bring your people into Gab AI with
                centrally managed seats and one shared pool of included credits.
              </p>
            </div>
            <div className="church-work-grid">
              {[
                [
                  "Centralized billing",
                  "Mix Plus and Plus Premium seats at their individual monthly prices. Manage paid team seats on an organization bill. Existing owner subscriptions may keep separate billing.",
                ],
                [
                  "Administrative control",
                  "Manage membership and paid seats, view organization and member usage, and set spending limits. Owners and admins have distinct roles.",
                ],
                [
                  "A shared credit pool",
                  "Every paid seat contributes its included credits to the team balance. Members keep their own accounts; private chats and files stay private.",
                ],
              ].map(([title, body]) => (
                <article key={title}>
                  <h3>{title}</h3>
                  <p>{body}</p>
                </article>
              ))}
            </div>
            <div className="uc-business-actions">
              <Cta href={business.primary?.href || "/business"}>
                {business.primary
                  ? "Create your organization"
                  : "Explore organization plans"}
              </Cta>
              <Link className="church-text-link" href="/docs/business">
                Read the organization guide →
              </Link>
            </div>
            {enrollment === "soon" && (
              <p className="church-fine">
                Organization enrollment is coming soon. Explore the plans and
                setup guide.
              </p>
            )}
          </section>
          {!!data.faqs?.length && (
            <section
              className="church-section church-faq"
              aria-labelledby="uc-faq"
            >
              <div>
                <p className="church-eyebrow">A few good questions</p>
                <h2 id="uc-faq">Before you begin.</h2>
              </div>
              <div>
                {data.faqs.map((faq) => (
                  <details key={faq.q}>
                    <summary>
                      {faq.q}
                      <span aria-hidden="true">+</span>
                    </summary>
                    <p>{faq.a}</p>
                  </details>
                ))}
              </div>
            </section>
          )}
          {!!relatedUseCases.length && (
            <nav
              className="church-section uc-related"
              aria-label="Related use cases"
            >
              <p className="church-eyebrow">Explore another angle</p>
              <h2>
                Built for the way
                <br />
                you work.
              </h2>
              <div>
                {relatedUseCases.map((item) => (
                  <Link href={`/use-cases/${item.slug}`} key={item.slug}>
                    {item.name}
                    <span aria-hidden="true">↗</span>
                  </Link>
                ))}
              </div>
            </nav>
          )}
          <section className="church-close">
            <p className="church-eyebrow">
              Gab AI for {data.name.toLowerCase()}
            </p>
            <h2>
              Your next idea
              <br />
              starts here.
            </h2>
            <div className="church-actions">
              <Cta href="/signup">{data.cta || "Try Gab AI free"}</Cta>
              <Cta href="/pricing" secondary>
                Compare plans
              </Cta>
            </div>
            <p className="church-fine">
              {FREE_USER_STARTING_CREDITS_DISPLAY} free credits · No credit card
              required to try Gab AI
            </p>
          </section>
        </div>
        <PublicFooter variant="editorial" showHero={false} />
      </main>
    </div>
  );
}
